// AccountingHealth — لوحة صحّة البيانات المحاسبية (3.4.9): استثناءات تُفسد التقارير.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useViewAudit } from '../../utils/viewAudit';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const SOURCE_LABEL = { vetclinic_records: ['العيادة', 'Clinic'], farrier_records: ['البيطار', 'Farrier'] };

function Bucket({ title, rows, t, navigate }) {
  const srcLabel = (s) => {
    const l = SOURCE_LABEL[s];
    return l ? t(l[1].toLowerCase(), l[0]) : s;
  };
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 900, mb: 1 }}>
        {title} <Chip size="small" label={rows.length} sx={{ ml: 1, bgcolor: rows.length ? C.roseSoft : C.cardSoft, color: rows.length ? C.rose : C.muted, fontWeight: 800 }} />
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: C.muted }}>{t('no_issues', 'لا استثناءات')} ✅</Typography>
      ) : (
        <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: C.cardSoft }}>
                {[t('source', 'المصدر'), t('date', 'التاريخ'), t('horse_label', 'الخيل'), t('amount', 'المبلغ'), ''].map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 900 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} hover={Boolean(r.link)} onClick={() => r.link && navigate(r.link)}
                  sx={{ cursor: r.link ? 'pointer' : 'default' }}>
                  <TableCell>{srcLabel(r.source)}</TableCell>
                  <TableCell>{r.record_date ? String(r.record_date).slice(0, 10) : '—'}</TableCell>
                  <TableCell>{r.horse_name || (r.horse_id ? `#${r.horse_id}` : '—')}</TableCell>
                  <TableCell>{r.amount ?? '—'}</TableCell>
                  <TableCell align="right">
                    {r.link ? (
                      <Tooltip title={t('open_record', 'فتح السجل')}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(r.link); }}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default function AccountingHealth() {
  const { t } = useTranslation();
  useViewAudit('accounting-health');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/accounting/health');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <SmartPage>
      <SmartHeader
        title={t('accounting_health', 'صحّة البيانات المحاسبية')}
        subtitle={t('accounting_health_hint', 'استثناءات قد تُفسد التقارير: سجلات بمبلغ صفر/ناقص، وسجلات بلا مالك قابل للحلّ.')}
      />

      <Box sx={{ mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} variant="outlined">{t('refresh', 'تحديث')}</Button>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : data ? (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
            <Chip label={`${t('total_issues', 'إجمالي الاستثناءات')}: ${data.summary.issues}`} sx={{ bgcolor: data.summary.issues ? C.rose : C.green, color: '#fff', fontWeight: 900 }} />
            <Chip label={`${t('zero_amount', 'مبلغ صفر/ناقص')}: ${data.summary.zero_amount}`} variant="outlined" />
            <Chip label={`${t('orphan_owner', 'بلا مالك قابل للحلّ')}: ${data.summary.orphan_owner}`} variant="outlined" />
          </Stack>
          <Bucket title={t('zero_amount_records', 'سجلات بمبلغ صفر أو ناقص')} rows={data.zeroAmount || []} t={t} navigate={navigate} />
          <Bucket title={t('orphan_owner_records', 'سجلات بلا مالك قابل للحلّ')} rows={data.orphanOwner || []} t={t} navigate={navigate} />
          <Typography variant="caption" sx={{ color: C.muted }}>{t('generated_at', 'وقت التوليد')}: {data.generated_at ? String(data.generated_at).slice(0, 19).replace('T', ' ') : '—'}</Typography>
        </>
      ) : null}
    </SmartPage>
  );
}
